import { CircleInfo } from "@/icons/CircleInfo.tsx";
import { MagnifyingGlass } from "@/icons/MagnifyingGlass.tsx";
import { getPhotoDownloadUrl } from "@/lib/helpers.ts";
import { LoraModel } from "@/models/lora.model.ts";
import { PhotoModel } from "@/models/photo.model.ts";
import { getPhotos } from "@/services/photo.service.ts";
import { Loading } from "@/ui/shared/Loading.tsx";
import { LoraSelect } from "@/ui/shared/LoraSelect.tsx";
import { PhotoCard } from "@/ui/shared/PhotoCard.tsx";
import { PhotoLightBox } from "@/ui/shared/PhotoLightBox.tsx";
import {
  Alert,
  Card,
  CloseButton,
  Input,
  Pagination,
  ScrollArea,
  SegmentedControl,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";

type Filter = "all" | "starred" | "liked";

export function Gallery() {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 400);
  const [filter, setFilter] = useState<Filter>("all");
  const [page, setPage] = useState<number>(1);
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);
  const [lora, setLora] = useState<LoraModel>();
  const [debouncedPage] = useDebouncedValue(page, 400);
  const [activePhotoIndex, setActivePhotoIndex] = useState<number | null>(null);
  const [viewerOpened, { open: openViewer, close: closeViewer }] =
    useDisclosure(false);

  const params = {
    page: debouncedPage,
    limit: 24,
    sort: "-created_at",
    "filter[type]": "output",
    "filter[filter]": filter,
    "filter[search]": debouncedSearch,
    "filter[from]": from,
    "filter[lora_id]": lora?.id,
    "filter[to]": to,
    include: "lora",
  };

  const { data, isFetching } = useQuery({
    queryKey: ["gallery", params],
    queryFn: () => getPhotos(params),
    retry: 1,
  });

  const photos: PhotoModel[] = data?.data || [];
  const [totalPhotos, setTotalPhotos] = useState(0);

  useEffect(() => {
    setTotalPhotos(data?.meta.total ?? 0);
  }, [data]);

  const handlePhotoClick = useCallback(
    (index: number) => {
      openViewer();
      setActivePhotoIndex(index);
    },
    [openViewer, setActivePhotoIndex],
  );

  return (
    <>
      <div className="flex flex-col gap-5">
        <div className="relative grid grid-cols-1 sm:grid-cols-1 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 md:gap-x-4 gap-y-4">
          <div className="col-span-1 relative">
            <div
              style={{
                top: "4.75rem",
              }}
              className="sticky"
            >
              <ScrollArea
                scrollbarSize={4}
                scrollbars="y"
                type="hover"
                offsetScrollbars={true}
              >
                <Card padding="lg" withBorder>
                  <Card.Section withBorder inheritPadding py="md">
                    <div className="flex flex-col gap-4">
                      <SegmentedControl
                        value={filter}
                        onChange={(value) => {
                          setPage(1);
                          setFilter(value as Filter);
                        }}
                        data={[
                          {
                            label: "All",
                            value: "all",
                          },
                          {
                            label: "Starred",
                            value: "starred",
                          },
                          {
                            label: "Liked",
                            value: "liked",
                          },
                        ]}
                      />

                      <LoraSelect
                        lora={lora}
                        setLora={setLora}
                        withCreateNew={false}
                      />

                      <DateInput
                        size="md"
                        value={from}
                        onChange={setFrom}
                        label="From"
                        placeholder="Photos generated after this date"
                        clearable
                      />

                      <DateInput
                        size="md"
                        value={to}
                        onChange={setTo}
                        label="To"
                        placeholder="Photos generated before this date"
                        clearable
                      />
                    </div>
                  </Card.Section>
                </Card>
              </ScrollArea>
            </div>
          </div>

          <div className="col-span-1 sm:col-span-1 md:col-span-2 lg:col-span-2 xl:col-span-3 2xl:col-span-4 flex flex-col gap-4">
            <div className="flex flex-col md:flex-row gap-2 w-full justify-start md:justify-between md:items-center">
              <Title order={3} fw={500}>
                Gallery ({totalPhotos || 0})
              </Title>

              <TextInput
                miw={300}
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
                leftSection={<MagnifyingGlass size={14} />}
                placeholder="Search photos by prompt..."
                rightSection={
                  search && (
                    <CloseButton
                      className="cursor-pointer"
                      aria-label="Clear input"
                      onClick={() => setSearch("")}
                    />
                  )
                }
              />
            </div>

            {isFetching && (
              <div className="flex flex-col w-full justify-center mb-4">
                <Loading />
              </div>
            )}

            {photos.length === 0 && !isFetching && (
              <div className="flex flex-col w-full justify-center mb-4">
                <Alert
                  bd="1px dashed"
                  color="grape"
                  icon={<CircleInfo size={20} />}
                  className="w-full"
                >
                  No photos found!
                </Alert>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
              {photos.map((photo, index) => (
                <motion.div
                  className="relative flex justify-center"
                  key={photo.id}
                  layout
                  whileHover={{ scale: 1.025 }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <PhotoCard
                    showApp={true}
                    showAI={false}
                    photo={photo}
                    onClick={() => handlePhotoClick(index)}
                  />
                </motion.div>
              ))}
            </div>

            {data && data.meta.total > 0 && (
              <div className="flex items-center justify-between gap-2">
                <Pagination
                  value={page}
                  onChange={setPage}
                  total={data.meta.last_page}
                  radius="md"
                  withEdges
                />

                <div className="flex flex-row flex-nowrap gap-2 items-center">
                  <Text>Page:</Text>
                  <Input
                    w={100}
                    className="text-center"
                    type="number"
                    value={page}
                    onChange={(event) => setPage(+event.currentTarget.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {data && (
        <PhotoLightBox
          viewerOpened={viewerOpened}
          activePhotoIndex={activePhotoIndex}
          closeViewer={closeViewer}
          slides={data.data.map((photo) => ({
            src: photo.url,
            downloadUrl: getPhotoDownloadUrl(photo),
            description: photo.prompt,
          }))}
        />
      )}
    </>
  );
}
